// src/components/ProfileSettingsModal.tsx
import React, { useEffect, useRef, useState } from "react";
import AvatarCircle from "./ui/AvatarCircle";
import AvatarPickerModal from "./AvatarPickerModal";
import { DEFAULT_VOICE_KEY, VOICES, VoiceKey } from "../config/voices";
import { X } from "lucide-react";

interface ProfileSettingsModalProps {
  open: boolean;
  onClose: () => void;

  nickname?: string | null;
  avatarId?: string | null;
  onAvatarChange: (newId: string) => void;

  // 🆕 notify parent (ChatContainer) immediately
  onVoiceKeyChange: (voiceKey: VoiceKey) => void;
}

const ACCESS_CODE_KEY = "accessCode";
const AUDIO_PLAYBACK_KEY = "audioPlayback";
const VOICE_KEY_STORAGE = "voiceKey";
const LEGACY_VOICE_ID_KEY = "voiceId";

const VOICE_PREVIEW_PATHS: Record<VoiceKey, string> = {
  bradley: "/audio/bradley/greeting.mp3",
  clarice: "/audio/clarice/greeting.mp3",
  jone: "/audio/jone/greeting.mp3",
  daniel: "/audio/daniel/greeting.mp3",
};

export default function ProfileSettingsModal({
  open,
  onClose,
  nickname,
  avatarId,
  onAvatarChange,
  onVoiceKeyChange, // 🆕 added
}: ProfileSettingsModalProps) {
  const [accessCode, setAccessCode] = useState("");
  const [audioPlayback, setAudioPlayback] = useState(false);
  const [voiceKey, setVoiceKey] = useState<VoiceKey>(DEFAULT_VOICE_KEY);

  const [previewingVoice, setPreviewingVoice] =
    useState<VoiceKey | null>(null);
  const [previewError, setPreviewError] = useState("");
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  const [pickerOpen, setPickerOpen] = useState(false);

  // Hydrate from localStorage on open (with legacy migration)
  useEffect(() => {
    if (!open) return;

    const savedAccess = localStorage.getItem(ACCESS_CODE_KEY) || "";
    const savedAudio = localStorage.getItem(AUDIO_PLAYBACK_KEY) === "true";

    let resolvedVoiceKey =
      (localStorage.getItem(VOICE_KEY_STORAGE) as VoiceKey | null);

    // 🔁 Backward compatibility: migrate old voiceId → voiceKey
    if (!resolvedVoiceKey) {
      const legacyVoiceId = localStorage.getItem(LEGACY_VOICE_ID_KEY);
      if (legacyVoiceId) {
        const match = Object.entries(VOICES).find(
          ([, v]) => v.elevenLabsVoiceId === legacyVoiceId
        );
        if (match) {
          resolvedVoiceKey = match[0] as VoiceKey;
          localStorage.setItem(VOICE_KEY_STORAGE, resolvedVoiceKey);
        }
      }
    }

    setAccessCode(savedAccess);
    setAudioPlayback(savedAudio);
    setVoiceKey(resolvedVoiceKey ?? DEFAULT_VOICE_KEY);
  }, [open]);

  // Stop any preview when the settings modal closes
  useEffect(() => {
    if (open) return;

    const audio = previewAudioRef.current;

    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      previewAudioRef.current = null;
    }

    setPreviewingVoice(null);
  }, [open]);

  // Stop preview audio if the component unmounts
  useEffect(() => {
    return () => {
      const audio = previewAudioRef.current;

      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, []);

  const stopVoicePreview = () => {
    const audio = previewAudioRef.current;

    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      previewAudioRef.current = null;
    }

    setPreviewingVoice(null);
  };

  const handleAudioPlaybackToggle = () => {
    if (audioPlayback) {
      stopVoicePreview();
      setPreviewError("");
    }

    setAudioPlayback((value) => !value);
  };

  const handleVoicePreview = () => {
    // Clicking Stop while this voice is playing
    if (previewingVoice === voiceKey && previewAudioRef.current) {
      stopVoicePreview();
      return;
    }

    stopVoicePreview();
    setPreviewError("");

    const audio = new Audio(VOICE_PREVIEW_PATHS[voiceKey]);

    previewAudioRef.current = audio;
    setPreviewingVoice(voiceKey);

    audio.onended = () => {
      if (previewAudioRef.current === audio) {
        previewAudioRef.current = null;
        setPreviewingVoice(null);
      }
    };

    audio.onerror = () => {
      if (previewAudioRef.current === audio) {
        previewAudioRef.current = null;
        setPreviewingVoice(null);
        setPreviewError("The voice preview could not be played.");
      }
    };

    audio.play().catch(() => {
      if (previewAudioRef.current === audio) {
        previewAudioRef.current = null;
        setPreviewingVoice(null);
        setPreviewError("The voice preview could not be played.");
      }
    });
  };

  const handleSave = () => {
    localStorage.setItem(ACCESS_CODE_KEY, accessCode.trim());
    localStorage.setItem(AUDIO_PLAYBACK_KEY, String(audioPlayback));
    localStorage.setItem(VOICE_KEY_STORAGE, voiceKey);

    // 🆕 immediately update live app state
    onVoiceKeyChange(voiceKey);

    // optional cleanup (safe to keep for now)
    // localStorage.removeItem(LEGACY_VOICE_ID_KEY);

    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <button
        aria-label="Close settings"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl ring-1 ring-black/5 dark:bg-zinc-900 dark:text-zinc-50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-settings-title"
      >
        {/* HEADER */}
        <div className="mb-4 flex items-center pr-12">
          <h2 id="profile-settings-title" className="text-xl font-semibold">
            Profile & Settings
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            className="absolute right-3 top-3 inline-flex h-11 w-11 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* AVATAR + NICKNAME SECTION */}
        <div className="flex flex-col items-center gap-3 mb-6">
          <AvatarCircle
            avatarId={avatarId || undefined}
            size={128}
            className="border border-zinc-300 shadow-sm"
          />

          {nickname && (
            <div className="text-base font-medium text-zinc-700 dark:text-zinc-200">
              {nickname}
            </div>
          )}

          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="mt-1 text-sm font-medium text-blue-600 hover:text-blue-700 underline"
          >
            Change avatar
          </button>
        </div>

        {/* SETTINGS FORM */}
        <form
          className="grid gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          {/* ACCESS CODE */}
          <div className="grid gap-2">
            <label
              htmlFor="accessCode"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-200"
            >
              Access Code
            </label>
            <input
              id="accessCode"
              type="text"
              autoComplete="one-time-code"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Enter your access code"
              className="w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-4 focus:ring-zinc-200 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-50 dark:focus:ring-zinc-800"
            />
            <p className="text-xs text-zinc-500">
              Used for secure syncing with your OCD Assistant.
            </p>
          </div>

          {/* AUDIO PLAYBACK */}
          <div className="grid gap-2">
            <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
              Read responses aloud
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={audioPlayback}
              onClick={handleAudioPlaybackToggle}
              className={
                "relative inline-flex h-7 w-12 items-center rounded-full transition " +
                (audioPlayback
                  ? "bg-zinc-900 dark:bg-zinc-100"
                  : "bg-zinc-300 dark:bg-zinc-700")
              }
            >
              <span
                className={
                  "ml-1 inline-block h-5 w-5 transform rounded-full bg-white shadow transition " +
                  (audioPlayback ? "translate-x-5" : "translate-x-0")
                }
              />
            </button>
            <p className="text-xs text-zinc-500">
              When on, responses in text chat are read aloud using your selected
              voice.
            </p>
          </div>

          {/* VOICE SELECTION */}
          {audioPlayback && (
            <div className="grid gap-2">
              <label
                htmlFor="voiceKey"
                className="text-sm font-medium text-zinc-700 dark:text-zinc-200"
              >
                Read-aloud voice
              </label>

              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                <select
                  id="voiceKey"
                  value={voiceKey}
                  onChange={(e) => {
                    stopVoicePreview();
                    setPreviewError("");
                    setVoiceKey(e.target.value as VoiceKey);
                  }}
                  className="min-w-0 w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-zinc-900 focus:outline-none focus:ring-4 focus:ring-zinc-200 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-50 dark:focus:ring-zinc-800"
                >
                  {Object.values(VOICES).map((v) => (
                    <option key={v.key} value={v.key}>
                      {v.label}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={handleVoicePreview}
                  aria-label={
                    previewingVoice === voiceKey
                      ? "Stop voice preview"
                      : "Test selected voice"
                  }
                  className="whitespace-nowrap rounded-xl border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                  {previewingVoice === voiceKey ? "Stop" : "Test voice"}
                </button>
              </div>

              <p className="text-xs text-zinc-500">
                Choose the voice used to read text chat responses aloud.
              </p>

              {previewError && (
                <p
                  role="alert"
                  className="text-xs text-red-600 dark:text-red-400"
                >
                  {previewError}
                </p>
              )}
            </div>
          )}

          {/* ACTION BUTTONS */}
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="rounded-xl bg-zinc-900 px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-800 active:scale-[.99] dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-200"
            >
              Save
            </button>
          </div>
        </form>
      </div>

      {/* Avatar picker modal */}
      <AvatarPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        currentAvatarId={avatarId || undefined}
        onSelect={(newId) => {
          onAvatarChange(newId);
          setPickerOpen(false);
        }}
      />
    </div>
  );
}
